// Fragment parsing + sessionStorage persistence. See README.md "Wire contract"
// for the exact fragment shape (#uir=<recording_id>.<token>).
//
// Security note: the fragment must be stripped from the URL IMMEDIATELY after
// parsing -- before persisting, before booting anything else -- so the token
// never lingers in the address bar, in SPA router state, in browser history,
// or gets forwarded to an analytics/error-reporting tool that logs the URL.

import { SESSION_STORAGE_KEY } from './config';
import type { Session } from './types';

interface PersistedSession {
  recording_id: string;
  token: string;
  startedAt: number;
  lastSeq: number;
  signalSeq: number;
}

/** Parse `#uir=<recording_id>.<token>`. Splits on the FIRST '.' only -- the
 * token itself is opaque and may contain dots. Returns null if the hash
 * doesn't carry a `uir=` fragment or the shape is malformed. */
export function parseFragment(hash: string): { recordingId: string; token: string } | null {
  if (!hash) return null;
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  const prefix = 'uir=';
  if (!raw.startsWith(prefix)) return null;
  const rest = raw.slice(prefix.length);
  const dot = rest.indexOf('.');
  if (dot <= 0 || dot === rest.length - 1) return null; // require non-empty id AND token
  return { recordingId: rest.slice(0, dot), token: rest.slice(dot + 1) };
}

/** Remove the fragment from the visible URL without adding a history entry. */
export function stripFragment(loc: Location = location, hist: History = history): void {
  hist.replaceState(null, '', loc.pathname + loc.search);
}

export function persistSession(session: Session, storage: Storage = sessionStorage): void {
  const record: PersistedSession = {
    recording_id: session.recordingId,
    token: session.token,
    startedAt: session.startedAt,
    lastSeq: session.lastSeq,
    signalSeq: session.signalSeq,
  };
  try {
    storage.setItem(SESSION_STORAGE_KEY, JSON.stringify(record));
  } catch {
    // sessionStorage unavailable (e.g. some private-browsing modes) -- the
    // session simply won't survive a full-page navigation; nothing else to do.
  }
}

export function resumeSession(storage: Storage = sessionStorage): Session | null {
  let raw: string | null;
  try {
    raw = storage.getItem(SESSION_STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<PersistedSession>;
    if (typeof parsed.recording_id !== 'string' || typeof parsed.token !== 'string') return null;
    return {
      recordingId: parsed.recording_id,
      token: parsed.token,
      startedAt: typeof parsed.startedAt === 'number' ? parsed.startedAt : Date.now(),
      lastSeq: typeof parsed.lastSeq === 'number' ? parsed.lastSeq : 0,
      signalSeq: typeof parsed.signalSeq === 'number' ? parsed.signalSeq : 0,
    };
  } catch {
    return null;
  }
}

export function clearSession(storage: Storage = sessionStorage): void {
  try {
    storage.removeItem(SESSION_STORAGE_KEY);
  } catch {
    // ignore
  }
}

/**
 * Entry point used by index.ts: fragment (if present) wins and is stripped +
 * persisted immediately; otherwise resume from sessionStorage; otherwise null
 * (script stays inert -- no fragment, no prior session on this origin).
 */
export function initSession(
  loc: Location = location,
  storage: Storage = sessionStorage,
  hist: History = history,
): Session | null {
  const parsed = parseFragment(loc.hash);
  if (parsed) {
    stripFragment(loc, hist);
    const session: Session = { recordingId: parsed.recordingId, token: parsed.token, startedAt: Date.now(), lastSeq: 0, signalSeq: 0 };
    persistSession(session, storage);
    return session;
  }
  return resumeSession(storage);
}
